import { escapeHtml, plainTextToEmailHtml } from "./emailBodyHtml.ts";

export type InboxReplyQuoteAnchor = {
  from_addr?: unknown;
  to_addr?: unknown;
  subject?: unknown;
  date_at?: unknown;
  body_html?: unknown;
  body_text?: unknown;
  snippet?: unknown;
};

export type InboxReplyBodyParts = {
  text: string;
  html: string;
};

export type InboxReplyBodyResult = InboxReplyBodyParts & {
  /** Quote appended after signature (Outlook-style: reply → signature → quote). */
  quote: InboxReplyBodyParts | null;
};

const QUOTE_SEPARATOR = "-----Ursprüngliche Nachricht-----";

const sanitizeNylasHtml = (rawHtml: string): string =>
  rawHtml
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/\son\w+='[^']*'/gi, "")
    .replace(/href="javascript:[^"]*"/gi, 'href="#"')
    .replace(/href='javascript:[^']*'/gi, "href='#'");

const htmlToPlainSnippet = (safeHtml: string): string =>
  safeHtml
    ? safeHtml
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/p>/gi, "\n\n")
        .replace(/<\/div>/gi, "\n")
        .replace(/<\/li>/gi, "\n")
        .replace(/<\/(ul|ol|blockquote|h[1-6])>/gi, "\n\n")
        .replace(/<[^>]+>/g, " ")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n[ \t]+/g, "\n")
        .replace(/[ \t]{2,}/g, " ")
        .replace(/\n{3,}/g, "\n\n")
        .trim()
        .slice(0, 120000)
    : "";

function formatQuoteDate(raw: unknown): string {
  const value = String(raw ?? "").trim();
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return new Intl.DateTimeFormat("de-DE", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Europe/Berlin",
    }).format(date);
  } catch {
    return date.toISOString();
  }
}

function resolveQuoteContent(anchor: InboxReplyQuoteAnchor): { html: string; text: string } {
  const rawHtml = String(anchor.body_html ?? "").trim();
  if (rawHtml) {
    const safeHtml = sanitizeNylasHtml(rawHtml);
    if (safeHtml) {
      return {
        html: safeHtml,
        text:
          htmlToPlainSnippet(safeHtml) ||
          String(anchor.body_text ?? "").trim() ||
          String(anchor.snippet ?? "").trim(),
      };
    }
  }

  const bodyText = String(anchor.body_text ?? "").trim();
  if (bodyText) {
    return {
      html: plainTextToEmailHtml(bodyText),
      text: bodyText,
    };
  }

  const snippet = String(anchor.snippet ?? "").trim();
  if (snippet) {
    return {
      html: plainTextToEmailHtml(snippet),
      text: snippet,
    };
  }

  return { html: "", text: "" };
}

export function buildInboxReplyQuoteBlocks(anchor: InboxReplyQuoteAnchor): InboxReplyBodyParts {
  const from = String(anchor.from_addr ?? "").trim();
  const to = String(anchor.to_addr ?? "").trim();
  const subject = String(anchor.subject ?? "").trim();
  const sentAt = formatQuoteDate(anchor.date_at);
  const content = resolveQuoteContent(anchor);

  const metaLines = [
    from ? `Von: ${from}` : null,
    sentAt ? `Gesendet: ${sentAt}` : null,
    to ? `An: ${to}` : null,
    subject ? `Betreff: ${subject}` : null,
  ].filter((line): line is string => Boolean(line));

  // Leading blank line before the Outlook-style separator.
  const textParts = ["", "", QUOTE_SEPARATOR, ...metaLines];
  if (content.text) {
    textParts.push("", content.text);
  }
  const text = textParts.join("\n");

  const htmlMeta = metaLines
    .map((line) => `<div style="margin:0 0 2px 0;color:#555;">${escapeHtml(line)}</div>`)
    .join("");
  const html = [
    `<div style="margin-top:1.25em;">`,
    `<div style="margin:0 0 8px 0;color:#555;">${escapeHtml(QUOTE_SEPARATOR)}</div>`,
    htmlMeta,
    content.html ? `<div style="margin-top:12px;">${content.html}</div>` : "",
    `</div>`,
  ].join("");

  return { text, html };
}

/**
 * Build reply body and optional quote.
 * Quote is returned separately so it can be appended after the signature
 * (Outlook order: reply → signature → quote).
 */
export function buildInboxReplyBody(params: {
  text: string;
  html: string;
  quoteEnabled: boolean;
  anchor: InboxReplyQuoteAnchor;
}): InboxReplyBodyResult {
  const text = String(params.text ?? "").trim();
  const html = String(params.html ?? "").trim();
  if (!params.quoteEnabled) {
    return { text, html, quote: null };
  }

  return {
    text,
    html,
    quote: buildInboxReplyQuoteBlocks(params.anchor),
  };
}
